import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Sale01 from '../components/sale/Sale01';
import { publicApi } from '../api/public-api';

const allFeatureLabels = {
  'academy:access': 'Akses Konten Akademi Dasar',
  'community:access': 'Gabung Grup Diskusi Umum',
  'journal:access': 'Investment Journal',
  'notification:market': 'Notifikasi Pasar',
  'mentoring:private': 'Sesi Mentoring 1-on-1',
  'academy:full': 'Akses Penuh Akademi & Materi',
  'ai:analysis': 'AI Analysis & Radar Pasar',
  'journal:premium': 'Investment Journal Premium',
  'early:access': 'Early Access Fitur Baru',
  'portfolio:consult': 'Konsultasi Portofolio Mingguan',
  'mentor:dashboard': 'Akses Dashboard Mentor',
  'teaching:tools': 'Tools Ajar & Materi Eksklusif',
  'certificate:report': 'Sertifikat & Laporan Progres',
};

const defaultPlans = [
  {
    name: 'Free Member', code: 'FREE', type: 'FREE', price: 0, duration: null,
    description: 'Mulai perjalanan investasi Anda',
    featureCodes: ['academy:access', 'community:access', 'journal:access', 'notification:market'],
    badge: 'Mulai', featured: false,
  },
  {
    name: 'VIP Member', code: 'PREMIUM', type: 'PREMIUM', price: 299000, duration: 30,
    description: 'Solusi lengkap untuk trader aktif',
    featureCodes: ['academy:access', 'community:access', 'journal:access', 'notification:market', 'mentoring:private', 'academy:full', 'ai:analysis', 'journal:premium', 'early:access'],
    badge: 'POPULER', featured: true,
  },
];

const planStyles = {
  FREE: { bg: 'rgba(88,189,125,0.08)', border: '#58bd7d', text: '#58bd7d', badge: '#58bd7d' },
  PREMIUM: { bg: 'rgba(12,143,134,0.08)', border: '#0C8F86', text: '#0C8F86', badge: '#0C8F86' },
};

function formatPrice(price) {
  if (price === 0) return 'Gratis';
  return `Rp ${price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
}

function extractFeatureCodes(plan) {
  if (plan.featureCodes) return { codes: plan.featureCodes, labels: allFeatureLabels };

  if (plan.permissions?.length) {
    const codes = plan.permissions.map((mp) => mp.permission?.code || mp.code).filter(Boolean);
    const labels = {};
    plan.permissions.forEach((mp) => {
      const c = mp.permission?.code || mp.code;
      const n = mp.permission?.name || mp.name;
      if (c) labels[c] = n || allFeatureLabels[c] || c;
    });
    return { codes, labels: { ...allFeatureLabels, ...labels } };
  }

  if (plan.features?.length) {
    const codes = plan.features.map((_, i) => `f:${i}`);
    const labels = {};
    plan.features.forEach((f, i) => { labels[`f:${i}`] = f; });
    return { codes, labels: { ...allFeatureLabels, ...labels } };
  }

  return { codes: [], labels: allFeatureLabels };
}

function getAllFeatureCodes(plans) {
  const codes = new Set();
  const labels = { ...allFeatureLabels };
  plans.forEach((p) => {
    const { codes: pc, labels: pl } = extractFeatureCodes(p);
    pc.forEach((c) => codes.add(c));
    Object.assign(labels, pl);
  });
  return { codes: Array.from(codes), labels };
}

function PlanIcon({ type, size = 24, color }) {
  const s = { width: size, height: size, color, flexShrink: 0 };
  if (type === 'FREE') {
    return (
      <svg {...s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
      </svg>
    );
  }
  if (type === 'PREMIUM') {
    return (
      <svg {...s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    );
  }
  return (
    <svg {...s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 19h20M4 19V7l5 4 3-6 3 6 5-4v12" />
    </svg>
  );
}

function PricingPage() {
  const [plans, setPlans] = useState([]);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const FE_WEB_URL = 'https://app.tradiora.id';

  useEffect(() => {
    publicApi.getPlans().then((res) => {
      if (res?.data?.length) setPlans(res.data);
    }).catch(() => {});
  }, []);

  const rawPlans = plans.length > 0 ? plans : defaultPlans;
  const nonTokenPlans = rawPlans.filter(p => !p.isTokenPlan);
  const displayPlans = nonTokenPlans.filter(
    (p) => p.type === 'FREE' || (billingCycle === 'monthly' ? p.duration === 30 || p.duration === null : p.duration === 365 || p.duration === null)
  );
  const { codes: allCodes, labels: featureLabels } = getAllFeatureCodes(nonTokenPlans);

  return (
    <div>
      <section style={{
        padding: '100px 0 80px',
        background: 'linear-gradient(135deg, #0a1628 0%, #1a0a2e 30%, #0C8F86 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: '-15%', right: '-8%', width: 350, height: 350,
          borderRadius: '50%', background: 'rgba(12,143,134,0.12)', filter: 'blur(60px)',
        }} />
        <div style={{
          position: 'absolute', bottom: '-15%', left: '-5%', width: 280, height: 280,
          borderRadius: '50%', background: 'rgba(12,143,134,0.08)', filter: 'blur(50px)',
        }} />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="row">
            <div className="col-md-12">
              <div className="block-text center">
                <h3 className="heading" style={{ color: '#fff' }}>Pilih Paket yang Tepat</h3>
                <p className="fs-20" style={{ color: 'rgba(255,255,255,0.7)', maxWidth: 600, margin: '0 auto 32px' }}>
                  Mulai perjalanan investasi Anda dengan paket yang sesuai kebutuhan
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section style={{ padding: '80px 0' }}>
        <div className="container">
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 48 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 12,
              background: 'var(--surface)', borderRadius: 40, padding: 4,
            }}>
              <button
                onClick={() => setBillingCycle('monthly')}
                style={{
                  padding: '8px 24px', borderRadius: 40, border: 'none', cursor: 'pointer',
                  fontSize: 14, fontWeight: 600,
                  background: billingCycle === 'monthly' ? 'var(--primary)' : 'transparent',
                  color: billingCycle === 'monthly' ? '#fff' : 'var(--text)',
                  transition: '0.2s all ease',
                }}
              >Monthly</button>
              <button
                onClick={() => setBillingCycle('yearly')}
                style={{
                  padding: '8px 24px', borderRadius: 40, border: 'none', cursor: 'pointer',
                  fontSize: 14, fontWeight: 600,
                  background: billingCycle === 'yearly' ? 'var(--primary)' : 'transparent',
                  color: billingCycle === 'yearly' ? '#fff' : 'var(--text)',
                  transition: '0.2s all ease',
                }}
              >Yearly</button>
            </div>
          </div>

          <div className="row" style={{ justifyContent: 'center' }}>
            {displayPlans.map((plan, index) => {
              const colors = planStyles[plan.type] || planStyles.PREMIUM;
              const isFeatured = plan.featured || plan.type === 'PREMIUM';
              const { codes: planCodes } = extractFeatureCodes(plan);

              return (
                <div className="col-xl-4 col-md-6" key={plan.code || plan.id || index} data-aos="fade-up" data-aos-delay={index * 100}>
                  <div style={{
                    background: 'var(--bg)',
                    borderRadius: 24,
                    border: isFeatured ? `2px solid ${colors.border}` : '1px solid var(--line)',
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    transition: '0.3s all ease-in-out',
                    boxShadow: isFeatured ? `0 8px 40px ${colors.border}22` : 'none',
                  }}>

                    <div style={{ padding: '32px 32px 0' }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        marginBottom: 8,
                      }}>
                        <PlanIcon type={plan.type} size={28} color={colors.text} />
                        <h5 style={{ fontSize: 20, margin: 0 }}>{plan.name}</h5>
                      </div>

                      <div style={{ margin: '16px 0' }}>
                        <span style={{ fontSize: 36, fontWeight: 700, color: 'var(--onsurface)' }}>
                          {typeof plan.price === 'number' ? formatPrice(plan.price) : plan.price}
                        </span>
                        {plan.duration ? (
                          <span style={{ fontSize: 14, color: 'var(--text)', marginLeft: 4 }}>
                            / {plan.duration} hari
                          </span>
                        ) : plan.type === 'FREE' && (
                          <span style={{ fontSize: 14, color: 'var(--text)', marginLeft: 4 }}>
                            ∞ Seumur Hidup
                          </span>
                        )}
                      </div>

                      <p style={{
                        fontSize: 14, color: 'var(--text)', marginBottom: 20,
                        lineHeight: 1.5, minHeight: 42,
                      }}>
                        {plan.description || ''}
                      </p>
                    </div>

                    <div style={{ padding: '0 32px', flex: 1 }}>
                      <p style={{
                        fontSize: 11, fontWeight: 700, color: 'var(--text)',
                        textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 16,
                      }}>
                        Fitur Includes
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {planCodes.map((code) => (
                          <div key={code} style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            fontSize: 13,
                          }}>
                            <span style={{
                              width: 20, height: 20, borderRadius: '50%',
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 10, fontWeight: 700, flexShrink: 0,
                              background: `${colors.text}18`,
                              color: colors.text,
                            }}>
                              ✓
                            </span>
                            <span style={{ color: 'var(--onsurface)' }}>
                              {featureLabels[code] || code}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div style={{ padding: '24px 32px 32px' }}>
                      <a
                        href={FE_WEB_URL}
                        className="btn-action"
                        style={{
                          width: '100%', textAlign: 'center', padding: '14px 20px', fontSize: 15,
                          background: isFeatured ? 'var(--primary)' : 'transparent',
                          border: isFeatured ? 'none' : '2px solid var(--line)',
                          color: isFeatured ? '#fff' : 'var(--onsurface)',
                        }}
                      >
                        {plan.type === 'FREE' ? 'Daftar Gratis' : 'Langganan Sekarang'}
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section style={{ padding: '0 0 80px' }}>
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <div style={{
                background: 'var(--surface)',
                borderRadius: 24,
                padding: '48px',
              }}>
                <div className="block-text center">
                  <h3 className="heading">Bandingkan Fitur</h3>
                  <p className="fs-20" style={{ color: 'var(--text)', maxWidth: 600, margin: '0 auto 32px' }}>
                    Lihat perbandingan lengkap fitur di setiap paket
                  </p>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid var(--line)' }}>
                        <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 700 }}>Fitur</th>
                        {nonTokenPlans.map((p) => (
                          <th key={p.code || p.id} style={{
                            padding: '16px 20px', textAlign: 'center', fontWeight: 700,
                            color: p.type === 'PREMIUM' ? 'var(--primary)' : 'var(--text)',
                          }}>{p.name}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {allCodes.map((code) => {
                        return (
                          <tr key={code} style={{ borderBottom: '1px solid var(--line)' }}>
                            <td style={{ padding: '14px 20px', fontSize: 14, fontWeight: 500 }}>
                              {featureLabels[code] || code}
                            </td>
                            {nonTokenPlans.map((p) => {
                              const { codes: pc } = extractFeatureCodes(p);
                              const included = pc.includes(code);
                              return (
                                <td key={p.code || p.id} style={{ padding: '14px 20px', textAlign: 'center', fontSize: 13 }}>
                                  {included ? (
                                    <span style={{ color: 'var(--primary)', fontWeight: 700 }}>✓</span>
                                  ) : (
                                    <span style={{ color: 'var(--text)', opacity: 0.3 }}>—</span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div style={{ background: 'linear-gradient(135deg, #141416 0%, #222630 100%)', padding: '80px 0' }}>
        <div className="container">
          <div className="row">
            <div className="col-md-12" style={{ textAlign: 'center' }}>
              <h3 style={{ color: '#fff', fontSize: 32, marginBottom: 16 }}>Butuh Bantuan?</h3>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 18, maxWidth: 500, margin: '0 auto 32px' }}>
                Tim kami siap membantu Anda memilih paket yang tepat sesuai kebutuhan.
              </p>
              <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Link to="/contact" className="btn-action">Hubungi Kami</Link>
                <Link to="/faq" className="btn-action style-2">FAQ</Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Sale01 />
    </div>
  );
}

export default PricingPage;
